import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BookOpen, FileText, FilterX, Plus, Trash2 } from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  AppBadge, AppButton, AppDataTable, ConfirmModal, EmptyState, FilterToolbar, IconButton, PageHeader,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import { passagesTrail } from "./quizCenterTrail";

/**
 * Passages — the reading passages and case documents that sets of questions
 * are asked about. Search and the type filter are sent to the server: the list
 * is paginated there, so filtering the visible page would hide matches.
 */

const PAGE_SIZE = 20;
const KIND_OPTIONS = [
  { value: "text", label: "Passage" },
  { value: "pdf", label: "Document (PDF)" },
];

const formatDate = (d) => (d
  ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
  : "");

export default function PassagesPage() {
  const navigate = useNavigate();
  const isLibrary = useLocation().pathname.startsWith("/sa/");
  const base = isLibrary ? "/sa/marketplace/quiz-questions" : "/quiz-bank";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [columnFilters, setColumnFilters] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const typed = typeof columnFilters.title === "string" ? columnFilters.title.trim() : "";
  // A request per keystroke would race itself; wait for a pause in the typing.
  const [search, setSearch] = useState(typed);
  useEffect(() => {
    const id = setTimeout(() => setSearch(typed), 300);
    return () => clearTimeout(id);
  }, [typed]);
  const kinds = Array.isArray(columnFilters.kind) ? columnFilters.kind : [];
  // One kind narrows the list; both (or none) is every passage.
  const kind = kinds.length === 1 ? kinds[0] : "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/quiz-passages", {
        params: { page, limit: PAGE_SIZE, search: search || undefined, kind: kind || undefined },
      });
      setRows(data.passages || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 0);
    } catch {
      setToast({ show: true, message: "Could not load passages.", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [page, search, kind]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, kind]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { data } = await apiClient.delete(`/quiz-passages/${deleteTarget._id}`);
      setToast({ show: true, message: data.message || "Passage deleted.", type: "success" });
      setDeleteTarget(null);
      load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Could not delete the passage.", type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const filtersOn = Object.keys(columnFilters).length > 0;
  const open = (p) => navigate(`${base}/passages/${p._id}`);

  const columns = [
    {
      key: "title",
      label: "Title",
      // Server-side: matches the title, the tags and the passage's own words.
      filter: { type: "text", placeholder: "Title or words in it…" },
      render: (p) => (
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
          {p.kind === "pdf"
            ? <FileText size={16} className="tw-shrink-0 tw-text-content-muted" aria-hidden="true" />
            : <BookOpen size={16} className="tw-shrink-0 tw-text-content-muted" aria-hidden="true" />}
          <span className="tw-truncate tw-font-medium tw-text-content">{p.title}</span>
        </span>
      ),
    },
    {
      key: "kind",
      label: "Type",
      filter: { type: "set", options: KIND_OPTIONS },
      render: (p) => (
        <AppBadge variant="secondary" size="sm">{p.kind === "pdf" ? "Document (PDF)" : "Passage"}</AppBadge>
      ),
    },
    {
      key: "questionCount",
      label: "Questions",
      align: "right",
      render: (p) => <span className="tw-tabular-nums">{p.questionCount || 0}</span>,
    },
    {
      key: "category",
      label: "Category",
      render: (p) => <span className="tw-text-content-muted">{p.category?.name || "—"}</span>,
    },
    {
      key: "updatedAt",
      label: "Updated",
      render: (p) => <span className="tw-whitespace-nowrap tw-text-content-muted">{formatDate(p.updatedAt)}</span>,
    },
    {
      key: "actions",
      label: "",
      pinned: "right",
      align: "right",
      render: (p) => (
        <IconButton
          icon={Trash2}
          label={`Delete ${p.title}`}
          variant="ghost-destructive"
          size="sm"
          onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); }}
        />
      ),
    },
  ];

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-4">
      <PageHeader
        breadcrumbs={passagesTrail({ isLibrary })}
        icon={BookOpen}
        title="Passages"
        description="Paragraphs and documents that a set of questions is asked about"
      />

      <AppDataTable
        toolbar={
          <FilterToolbar
            flush
            actions={
              <AppButton size="sm" onClick={() => navigate(`${base}/passages/new`)}>
                <Plus size={16} /> New passage
              </AppButton>
            }
          >
            <span className="tw-text-[0.8125rem] tw-text-content-subtle">
              {total} passage{total === 1 ? "" : "s"}
            </span>
            {filtersOn && (
              <AppButton size="sm" variant="outline" onClick={() => setColumnFilters({})}>
                <FilterX size={14} /> Clear filters
              </AppButton>
            )}
          </FilterToolbar>
        }
        columns={columns}
        rows={rows}
        rowKey={(p) => p._id}
        loading={loading}
        onRowClick={open}
        columnFilters={columnFilters}
        onColumnFilterChange={(key, value) =>
          setColumnFilters((prev) => {
            const next = { ...prev };
            if (value === undefined) delete next[key];
            else next[key] = value;
            return next;
          })
        }
        emptyState={
          filtersOn ? undefined : (
            <EmptyState
              icon={BookOpen}
              title="No passages yet"
              description="Write a paragraph or upload a document, then add the questions students answer about it — reading comprehension, medical coding cases, data interpretation."
              action={
                <AppButton onClick={() => navigate(`${base}/passages/new`)}>
                  <Plus size={16} /> New passage
                </AppButton>
              }
            />
          )
        }
        emptyMessage="No passage matches these filters."
        pagination={totalPages > 1 ? {
          page,
          totalPages,
          total,
          pageSize: PAGE_SIZE,
          onChange: setPage,
          infoLabel: "passages",
        } : undefined}
      />

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        loading={deleting}
        title="Delete this passage?"
        message={
          deleteTarget?.questionCount
            ? `“${deleteTarget.title}” and its ${deleteTarget.questionCount} question${deleteTarget.questionCount === 1 ? "" : "s"} will be removed from the bank. Exams already taken keep their copy.`
            : `“${deleteTarget?.title}” will be removed from the bank.`
        }
        confirmText="Delete"
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
}
